import React, { useState, useEffect, useCallback } from 'react';
import classnames from 'classnames';
import { getCssDefaultVariablesConfig, saveCssVariablesConfig } from './api';
import styles from './css-variables.module.scss';
import TextField from '../../common/inputs/text-field';
import { get } from 'lodash-es';
import { CssVariablesList, CSS_TYPES } from './types';

type Props = {
  spotId: string;
  spotName: string;
};

const CssVariables: React.FC<Props> = ({ spotId, spotName }) => {
  const [isFetching, setIsFetching] = useState<boolean>(true);
  const [cssVariables, setCssVariables] = useState<CssVariablesList>({});
  const [customCssVariables, setCustomCssVariables] = useState<CssVariablesList>({});
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [isFinishedUploading, setIsFinishedUploading] = useState<boolean>(false);
  const [isErrorUploading, setIsErrorUploading] = useState<boolean>(false);
  const [isErrorFetching, setIsErrorFetching] = useState<boolean>(false);
  
  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await getCssDefaultVariablesConfig(spotId);

        const cssVars = {
          [CSS_TYPES.DEFAULT]: res[CSS_TYPES.DEFAULT] || {},
          [CSS_TYPES.DARK]: res[CSS_TYPES.DARK] || {},
          [CSS_TYPES.MOBILE]: res[CSS_TYPES.MOBILE] || {}
        }
  
        setCssVariables(cssVars);
      } catch (error) {
        setIsErrorFetching(true);
      }
      setIsFetching(false);
    }

    fetch();
  }, []);

  const setField = useCallback((type: string, key: string) => (e: string) => {
    const currentValues = {
      ...cssVariables, 
      [type]: {
        ...cssVariables[type],
        [key]: {
          ...cssVariables[type][key],
          value: e
        }
      }
    };
    const customValues = {
      ...customCssVariables,
      [type]: {
        ...customCssVariables[type],
        [key]: {
          ...get(cssVariables, `${type}.${key}`),
          value: e
        }
      }
    };
    
    setCssVariables(currentValues)
    setCustomCssVariables(customValues);
  }, [cssVariables, customCssVariables])

  const isHexColor = useCallback((text: string): boolean => {
    return /^#([0-9a-f]{6}|[0-9a-f]{3})$/i.test(text);
  }, [])
  
  const getVariables = useCallback((type: string) => {
    return Object.keys(cssVariables[type]).map(key => {
      // Check if the current value is color hex value
      let comp = isHexColor(cssVariables[type][key].value) ?
        <div key={key} className={styles.colorPickerContainer}>
          <span className={styles.colorPickerTitle}>{key}</span>
          <input type="color" value={cssVariables[type][key].value} id={key} onChange={(e) => {setField(type, key)(e.target.value)}}/>
          <label className={styles.colorPickerText} htmlFor={key}>{cssVariables[type][key].value}</label>
        </div>
      :
        <TextField
          key={key}
          type="text"
          className="outside"
          onChange={setField(type, key)}
          title={key}
          maxLength={50}
          value={cssVariables[type][key].value}
        />;
      
      return comp;
    })
  }, [cssVariables])

  const saveSettings = useCallback(async () => {
      setIsErrorUploading(false);
      setIsFinishedUploading(false);
      setIsUploading(true);

      try {
        await saveCssVariablesConfig(spotId, spotName, customCssVariables);
        setIsFinishedUploading(true);
      } catch (error) {
        setIsErrorUploading(true);
      }

      setIsUploading(false);
    }, [spotId, spotName, customCssVariables])

  const saveBtnClassName = classnames('button-green-medium', styles.saveBtn);
  const saveSpinnerClassName = classnames('spinner', 'loading-messages', styles.saveSpinner);

  if (isErrorFetching) {
    return (
      <div className={styles.spinnerContainer}>
        <span className={styles.errorMessage}>
          An error has occurred while fetching data. Please try again. 
        </span>
      </div>
    )
  }

  return (
    isFetching ?
      <div className={styles.spinnerContainer}>
        <span className="spinner loading-messages" />
      </div>
    :
      <>
        <h2 className={styles.title}>Default Values</h2>
        {getVariables(CSS_TYPES.DEFAULT)}
        <h2 className={styles.title}>Dark Mode</h2>
        {getVariables(CSS_TYPES.DARK)}
        <h2 className={styles.title}>Mobile</h2>
        {getVariables(CSS_TYPES.MOBILE)}
        <div className={styles.saveContainer}>
          <button
            className={saveBtnClassName}
            onClick={saveSettings}
          >
            Save Settings
          </button>
          {isUploading && 
            <span className={saveSpinnerClassName} />
          }
          {isFinishedUploading &&
            <span className={styles.successMessage}>Saved Successfully!</span>
          }
          {isErrorUploading &&
            <span className={styles.errorMessage}>Failed Uploading!</span>
          }
        </div>
      </>
  )
};

export default CssVariables;
